import * as React from 'react';
import {useMemo} from 'react';
import {Platform, View} from 'react-native';
import {useDispatch} from 'react-redux';
import {productsSlice} from '../../../redux/reducers/products';
import {Props} from './model';
import {ProductResponsePayload} from '../../../shared/models/api/productResponsePayload';
import {BreadcrumbItem} from '../../../shared/components/breadcrumbs/model';
import {Routes, Screens} from '../../../shared/routing';
import {DeviceSize, useDeviceSize} from '../../../shared/theme/helpers';
import {useStyles} from '../../../shared/hooks/styles';
import Screen from '../../../shared/components/screen';
import {CustomScrollView} from '../../../shared/components/scrollView';
import {LoadableContent} from '../../../shared/components/loadableContent';
import {Breadcrumbs} from '../../../shared/components/breadcrumbs';
import {ProductCard} from '../ProductCard/ProductCard';
import {ProductDataCard} from './components/ProductDataCard/ProductDataCard';

import {ProductGeoMap} from './components/ProductGeoMap/ProductGeoMap';
import {HttpWrapperOptions, httpService} from '../../../shared/httpWrapper';
import {getApiRoute} from '../../../shared/pipes';
import {apiUrls} from '../../../shared/constants/urls';
import {ProductDetailedResponsePayload} from '../../../shared/models/api/productDetailedResponsePayload';
import {useScrollViewMap} from '../../../shared/components/yaMap/mobile/useScrollViewMap';

const getRoutes = (product: ProductResponsePayload): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: 'Возможности карты',
      routeName: Screens.CardServices,
      webRoute: Routes.cardService,
    },
    {
      title: 'Магазин поощрений',
      routeName: Screens.RewardShop,
      webRoute: Routes.rewardShop,
    },
  ];
  if (product) {
    breadcrumbs.push({
      title: product.name ?? '',
      routeName: Screens.ProductItem,
      webRoute: Routes.productItem,
    });
  }
  return breadcrumbs;
};

export default ({product, id, productAcceptors}: Props) => {
  const deviceSize = useDeviceSize();
  const dispatch = useDispatch();

  const styles = useStyles(({mediaOnly, theme, mediaExclude}) => {
    return {
      screen: {},
      sectionContainer: {
        ...mediaOnly(DeviceSize.mobile, {paddingHorizontal: 16, paddingBottom: 50}, {}),
        ...mediaOnly(DeviceSize.tablet, {paddingHorizontal: 35, paddingBottom: 50}, {}),
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 60, paddingBottom: 100}, {}),
        marginTop: 32,
        backgroundColor: theme.screen.container[deviceSize].backgroundColor,
      },
      acceptorDescription: {
        color: theme.color.textColor,
        ...mediaOnly(DeviceSize.desktop, {...theme.fonts.h5Light}, {...theme.fonts.additionalText}),
      },
      acceptorLayout: mediaOnly(DeviceSize.desktop, {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginTop: 38,
      }),
      acceptorLayoutASide: {
        marginBottom: 20,
        ...mediaOnly(DeviceSize.desktop, {
          flexBasis: 315,
          flexShrink: 0,
          flexGrow: 0,
          marginRight: 20,
        }),
      },
      acceptorLayoutMain: mediaOnly(DeviceSize.desktop, {
        flexShrink: 1,
        flexGrow: 1,
      }),
      dataCardWrapper: {
        marginBottom: 20,
      },
      dataCardGeomapWrapper: mediaOnly(
        DeviceSize.desktop,
        {
          marginBottom: 80,
        },
        {
          marginBottom: 40,
        },
      ),
      header: {
        position: 'relative',
        zIndex: 1,
        ...mediaOnly(
          DeviceSize.desktop,
          {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 45,
          },
          {
            marginBottom: 24,
          },
        ),
      },
      headerTitle: mediaOnly(DeviceSize.desktop, {marginRight: 24}, {marginBottom: 24}),
      acceptorsButtonContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        ...mediaOnly(DeviceSize.mobile, {}, {paddingHorizontal: 10}),
      },
      acceptorsFilterButtonMobile: {
        width: '100%',
        marginTop: 10,
        marginBottom: 24,
        marginLeft: 0,
        marginRight: 0,
        ...mediaExclude(DeviceSize.mobile, {maxWidth: 328}),
      },
      loyalityHeader: {
        marginTop: 30,
        fontFamily: 'GothamPro-Medium',
        ...mediaExclude(DeviceSize.mobile, theme.fonts.h2Light),
        ...mediaOnly(DeviceSize.mobile, theme.fonts.action),
      },
      itemCard: {
        ...(Platform.OS === 'web' ? {height: `100%`, width: `100%`} : {width: '100%', alignSelf: 'center'}),
      },
    };
  });

  const routes = useMemo(() => getRoutes(product), [product]);
  const [detailedProduct, setDetailedProduct] = React.useState(product);

  const {scrollViewProps, mapProps} = useScrollViewMap();

  const requestProductDetails = React.useCallback(async () => {
    const productDetails = await httpService.get<ProductDetailedResponsePayload>(
      getApiRoute(apiUrls.getProductDetails, {
        productId: id,
      }),
      new HttpWrapperOptions().Wait(false).Errors(true).ThrowException(false),
    );
    setDetailedProduct(productDetails);
  }, [id, product]);

  React.useEffect(() => {
    requestProductDetails();

    return () => {
      dispatch(productsSlice.actions.setDetailedProduct(null));
    };
  }, [id, product]);

  return (
    <Screen style={styles.screen} key={Platform.OS === 'web' ? id : ''}>
      <CustomScrollView contentInsetAdjustmentBehavior='automatic' {...scrollViewProps}>
        <LoadableContent loading={!detailedProduct}>
          <View style={styles.sectionContainer}>
            <Breadcrumbs routes={routes} />
            <View style={styles.acceptorLayout}>
              <View style={styles.acceptorLayoutASide}>
                <ProductCard
                  product={detailedProduct}
                  style={styles.itemCard}
                  nameRows={4}
                  shortDescriptionRows={3}
                  flexHeight={true}
                />
              </View>
              <View style={styles.acceptorLayoutMain}>
                <View style={styles.dataCardWrapper}>
                  <ProductDataCard
                    headerText={detailedProduct?.name ?? ''}
                    fullDescription={detailedProduct?.fullDescription ?? ''}
                    howToGet={detailedProduct?.howToGet ?? ''}
                    aboutAcceptor={detailedProduct?.aboutAcceptor ?? ''}
                  />
                  <View style={styles.dataCardGeomapWrapper}>
                    {productAcceptors && (
                      <ProductGeoMap
                        mapProps={mapProps}
                        product={detailedProduct}
                        productAcceptors={productAcceptors}
                      />
                    )}
                  </View>
                </View>
              </View>
            </View>
          </View>
        </LoadableContent>
      </CustomScrollView>
    </Screen>
  );
};
